import { createContext, useContext } from 'react'

export interface AuthUser {
  email: string
  id: string
  image?: string | null
  name?: string | null
  role?: string
  [key: string]: unknown
}

export interface AuthSession {
  user: AuthUser
}

export interface StandaloneSignupInput {
  displayName: string
  password: string
  recoveryEmail?: string
  timeZone?: string
  username: string
}

interface AuthContextType {
  loading: boolean
  /** Emails a password-reset link to the account's recovery address. */
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>
  /** Completes a password reset with the token from the emailed link. */
  resetPassword: (input: { token: string; newPassword: string }) => Promise<{ error: Error | null }>
  session: AuthSession | null
  /**
   * Redirects the browser to Google's OAuth screen for primary sign-in or
   * primary reconnect. Pass `{ isReconnect: true }` to mark the callback as a
   * reconnect (read by `auth-callback.tsx` via the `?reconnect=true` query
   * param on the redirect URL).
   */
  signInWithGoogle: (opts?: { isReconnect?: boolean }) => Promise<{ error: Error | null }>
  /** Logs a returning standalone user in with their @langmail.me address + password. */
  signInWithLangmail: (input: { email: string; password: string }) => Promise<{ error: Error | null }>
  signInWithTestAccount: () => Promise<{ error: unknown }>
  signOut: () => Promise<void>
  /**
   * Creates a standalone @langmail.me account via Better Auth email/password.
   * The chosen `username` becomes both the login email (`username@langmail.me`)
   * and the native mailbox address. On success the user is signed in, but the
   * mailbox provisions asynchronously — callers poll status before landing.
   */
  signUpWithLangmail: (input: StandaloneSignupInput) => Promise<{ error: Error | null }>
  user: AuthUser | null
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
